import ProgramPanel from "@/components/programs/ProgramPanel";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, MessageCircle, Users, Send, Trash2, ArrowLeft, Lock, UserCheck,
  Check, ShieldCheck, LogOut, MessagesSquare, FileText, Image, Film, Paperclip, X,
  CalendarDays, CalendarPlus, MapPin, Share2, Radio, PlayCircle, Eye,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { toEmbedUrl } from "@/lib/video";
import { useAuth } from "@/context/AuthContext";
import { GroupIcon } from "@/pages/Community";

import RichTextEditor from "@/components/RichTextEditor";
import RichContent from "@/components/RichContent";
const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

const Avatar = ({ name }) => (
  <span className="grid place-items-center h-9 w-9 rounded-full bg-[#144F75]/10 text-[#144F75] text-sm font-semibold shrink-0">
    {(name || "?").slice(0, 1).toUpperCase()}
  </span>
);

function Comments({ groupId, postId }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const { data: comments = [] } = useQuery({
    queryKey: ["comments", postId],
    queryFn: () => api.get(`/groups/${groupId}/posts/${postId}/comments`).then((r) => r.data),
  });
  const addMut = useMutation({
    mutationFn: () => api.post(`/groups/${groupId}/posts/${postId}/comments`, { body }),
    onSuccess: () => { setBody(""); qc.invalidateQueries({ queryKey: ["comments", postId] }); qc.invalidateQueries({ queryKey: ["posts", groupId] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail?.message || e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: (cid) => api.delete(`/groups/${groupId}/posts/${postId}/comments/${cid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["comments", postId] }); qc.invalidateQueries({ queryKey: ["posts", groupId] }); },
  });

  return (
    <div className="mt-3 pl-12 space-y-3">
      {comments.map((c) => (
        <div key={c.id} className="flex gap-2 items-start" data-testid={`comment-${c.id}`}>
          <Avatar name={c.author_name} />
          <div className="flex-1 rounded-xl bg-[#F9F7F3] px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-[#1A242B]">{c.author_name} <span className="text-[#8FA89B] font-normal">· {timeAgo(c.created_at)}</span></span>
              {c.can_delete && <button onClick={() => delMut.mutate(c.id)} data-testid={`comment-del-${c.id}`} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>}
            </div>
            <p className="text-sm text-[#4A5D6B] mt-0.5 whitespace-pre-wrap">{c.body}</p>
          </div>
        </div>
      ))}
      <div className="flex gap-2 items-center">
        <input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && body.trim() && addMut.mutate()}
          placeholder="Write a comment…" data-testid={`comment-input-${postId}`} className="eyw-input flex-1 !py-1.5 text-sm" />
        <button onClick={() => body.trim() && addMut.mutate()} disabled={addMut.isPending} data-testid={`comment-send-${postId}`} className="eyw-btn !py-1.5 !px-3"><Send size={14} /></button>
      </div>
    </div>
  );
}

function Discussion({ group }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canFormat = !!user && (["admin", "staff", "enhancer"].includes(user.role) || !!user.manages_location_id);
  const [body, setBody] = useState("");
  const [openPost, setOpenPost] = useState(null);
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["posts", group.id],
    queryFn: () => api.get(`/groups/${group.id}/posts`).then((r) => r.data),
  });
  const postMut = useMutation({
    mutationFn: () => api.post(`/groups/${group.id}/posts`, { body }),
    onSuccess: () => { setBody(""); qc.invalidateQueries({ queryKey: ["posts", group.id] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail?.message || e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: (pid) => api.delete(`/groups/${group.id}/posts/${pid}`),
    onSuccess: () => { toast.success("Post removed"); qc.invalidateQueries({ queryKey: ["posts", group.id] }); },
  });

  return (
    <div>
      <div className="eyw-card p-4 mb-6">
        {canFormat ? (
          <RichTextEditor value={body} onChange={setBody} testid="post-composer" placeholder={`Share something with ${group.name}…`} minHeight={90} enableBlocks={false} />
        ) : (
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} data-testid="post-composer"
            placeholder={`Share something with ${group.name}…`} className="eyw-input w-full resize-none" />
        )}
        <div className="flex justify-end mt-2">
          <button onClick={() => body.trim() && postMut.mutate()} disabled={postMut.isPending || !body.trim()} data-testid="post-submit" className="eyw-btn !py-2 text-sm disabled:opacity-50">
            {postMut.isPending ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />} Post
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-16 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
      ) : posts.length === 0 ? (
        <p className="text-center text-[#4A5D6B] py-16">No posts yet. Start the conversation.</p>
      ) : (
        <div className="space-y-4" data-testid="posts-list">
          {posts.map((p) => (
            <div key={p.id} className="eyw-card p-5" data-testid={`post-${p.id}`}>
              <div className="flex gap-3 items-start">
                <Avatar name={p.author_name} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#1A242B]">{p.author_name} <span className="text-xs text-[#8FA89B] font-normal capitalize">· {p.author_role} · {timeAgo(p.created_at)}</span></span>
                    {p.can_delete && <button onClick={() => delMut.mutate(p.id)} data-testid={`post-del-${p.id}`} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>}
                  </div>
                  {p.announcement && <span className="inline-block mt-1 text-[10px] uppercase tracking-wider bg-[#D98E73] text-white rounded-full px-2 py-0.5" data-testid={`post-announcement-${p.id}`}>Announcement</span>}
                  {p.rich ? <RichContent text={p.body} className="text-[#1A242B] mt-1 leading-relaxed" testid={`post-body-${p.id}`} />
                    : <p className="text-[#1A242B] mt-1 whitespace-pre-wrap leading-relaxed">{p.body}</p>}
                  <button onClick={() => setOpenPost(openPost === p.id ? null : p.id)} data-testid={`post-comments-toggle-${p.id}`} className="text-xs text-[#144F75] mt-2 inline-flex items-center gap-1">
                    <MessageCircle size={13} /> {p.comment_count} {p.comment_count === 1 ? "comment" : "comments"}
                  </button>
                </div>
              </div>
              {openPost === p.id && <Comments groupId={group.id} postId={p.id} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Members({ group }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isCouncil = ["admin", "council", "staff"].includes(user?.role);
  const { data, isLoading } = useQuery({
    queryKey: ["group-members", group.id],
    queryFn: () => api.get(`/groups/${group.id}/members`).then((r) => r.data),
  });
  const act = useMutation({
    mutationFn: ({ uid, action, host }) => api.post(`/groups/${group.id}/members/${uid}/${action}`, host !== undefined ? { host } : {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["group-members", group.id] }); qc.invalidateQueries({ queryKey: ["group", group.slug] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading) return <div className="grid place-items-center py-16 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>;
  const members = data?.members || [];
  const isHost = data?.is_host;

  return (
    <div className="eyw-card divide-y divide-[#E5E0D8]" data-testid="members-roster">
      {members.map((m) => (
        <div key={m.id} className="flex items-center gap-3 p-4" data-testid={`roster-${m.user_id}`}>
          <Avatar name={m.user_name} />
          <div className="flex-1 min-w-0">
            <Link to={`/community/members/${m.user_id}`} className="text-sm font-semibold text-[#1A242B] hover:text-[#144F75]">{m.user_name}</Link>
            <p className="text-xs text-[#8FA89B] capitalize">{m.user_role}{m.role_in_group === "host" && " · host"}{m.status === "pending" && " · pending"}</p>
          </div>
          {isHost && m.status === "pending" && (
            <button onClick={() => act.mutate({ uid: m.user_id, action: "approve" })} data-testid={`approve-${m.user_id}`} className="eyw-btn !py-1.5 !px-3 text-xs"><Check size={13} /> Approve</button>
          )}
          {isCouncil && m.role_in_group !== "host" && m.status === "active" && (
            <button onClick={() => act.mutate({ uid: m.user_id, action: "host", host: true })} data-testid={`makehost-${m.user_id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><ShieldCheck size={13} /> Make host</button>
          )}
          {isHost && (
            <button onClick={() => act.mutate({ uid: m.user_id, action: "remove" })} data-testid={`remove-${m.user_id}`} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
          )}
        </div>
      ))}
      {members.length === 0 && <p className="text-center text-[#4A5D6B] py-10">No members yet.</p>}
    </div>
  );
}

function GroupChat({ group }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [att, setAtt] = useState(null); // {url, kind, name}
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const imgRef = useRef(null);
  const fileRef = useRef(null);
  const { data, isLoading } = useQuery({
    queryKey: ["group-chat", group.id],
    queryFn: () => api.get(`/groups/${group.id}/chat`).then((r) => r.data),
    refetchInterval: 8000,
  });
  const messages = data?.messages || [];
  const isHost = data?.is_host;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAtt(res);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "We couldn't upload that file. Please check it and try again."); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const sendMut = useMutation({
    mutationFn: () => api.post(`/groups/${group.id}/chat`, {
      body: text.trim(),
      image_url: att?.kind === "image" ? att.url : "",
      video_url: att?.kind === "video" ? att.url : "",
      file_url: att && att.kind !== "image" && att.kind !== "video" ? att.url : "",
      file_name: att && att.kind !== "image" && att.kind !== "video" ? att.name : "",
    }),
    onSuccess: () => { setText(""); setAtt(null); qc.invalidateQueries({ queryKey: ["group-chat", group.id] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail?.message || e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: (mid) => api.delete(`/groups/${group.id}/chat/${mid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-chat", group.id] }),
  });
  const canSend = (text.trim() || att) && !uploading;

  return (
    <div className="eyw-card flex flex-col h-[62vh]" data-testid="group-chat">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="grid place-items-center py-16 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
        ) : messages.length === 0 ? (
          <p className="text-center text-[#8FA89B] text-sm py-16">No messages yet — say hello to the circle 👋</p>
        ) : messages.map((m) => {
          const mine = m.author_id === user?.id;
          const isVid = /\.(mp4|webm|mov|m4v|ogv)$/i.test(m.video_url || "");
          return (
            <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`group-msg-${m.id}`}>
              <Avatar name={m.author_name} />
              <div className={`max-w-[75%] group ${mine ? "text-right" : ""}`}>
                <div className={`rounded-2xl px-4 py-2 inline-block text-left ${mine ? "bg-[#144F75] text-white rounded-br-sm" : "bg-[#F4F1EB] text-[#1A242B] rounded-bl-sm"}`}>
                  {!mine && <span className="block text-[11px] font-semibold text-[#144F75]">{m.author_name}</span>}
                  {m.image_url && <img src={m.image_url} alt="" className="rounded-lg mb-1 max-h-56" />}
                  {m.video_url && isVid && <video src={m.video_url} controls className="rounded-lg mb-1 max-h-56 bg-black" />}
                  {m.file_url && <a href={m.file_url} target="_blank" rel="noreferrer" className={`flex items-center gap-1.5 text-sm mb-1 underline ${mine ? "text-white" : "text-[#144F75]"}`}><FileText size={14} /> {m.file_name || "Download"}</a>}
                  {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                </div>
                <div className={`flex items-center gap-2 mt-0.5 ${mine ? "justify-end" : ""}`}>
                  <span className="text-[10px] text-[#8FA89B]">{timeAgo(m.created_at)}</span>
                  {(mine || isHost) && <button onClick={() => delMut.mutate(m.id)} data-testid={`group-msg-del-${m.id}`} className="text-[#B9C4BD] hover:text-red-500"><Trash2 size={11} /></button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {att && (
        <div className="mx-3 mb-1 flex items-center gap-2 bg-[#F4F1EB] rounded-lg px-3 py-2 text-sm" data-testid="group-chat-attachment">
          {att.kind === "image" ? <Image size={15} className="text-[#144F75]" /> : att.kind === "video" ? <Film size={15} className="text-[#144F75]" /> : <FileText size={15} className="text-[#144F75]" />}
          <span className="flex-1 truncate text-[#1A242B]">{att.name}</span>
          <button onClick={() => setAtt(null)} className="text-[#8FA89B] hover:text-red-500"><X size={14} /></button>
        </div>
      )}
      <div className="border-t border-[#E5E0D8] p-3 flex gap-2 items-center">
        <button onClick={() => imgRef.current?.click()} disabled={uploading} data-testid="group-chat-photo" className="text-[#144F75] hover:text-[#0d3a57] disabled:opacity-50" title="Photo/video">{uploading ? <Loader2 className="animate-spin" size={18} /> : <Image size={18} />}</button>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="group-chat-file" className="text-[#144F75] hover:text-[#0d3a57] disabled:opacity-50" title="File"><Paperclip size={18} /></button>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && canSend && sendMut.mutate()}
          data-testid="group-chat-input" placeholder={`Message ${group.name}…`} className="eyw-input flex-1" />
        <button onClick={() => canSend && sendMut.mutate()} disabled={sendMut.isPending || !canSend} data-testid="group-chat-send" className="eyw-btn !px-4 disabled:opacity-50">
          {sendMut.isPending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        </button>
        <input ref={imgRef} type="file" accept="image/*,video/*" onChange={upload} className="hidden" />
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.csv,.txt,.ppt,.pptx,.xls,.xlsx" onChange={upload} className="hidden" />
      </div>
    </div>
  );
}

function EventAttendees({ eventId }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["event-attendees", eventId],
    queryFn: () => api.get(`/group-events/${eventId}/attendees`).then((r) => r.data),
    enabled: open,
  });
  return (
    <div className="mt-3">
      <button onClick={() => setOpen((v) => !v)} data-testid={`event-attendees-toggle-${eventId}`} className="text-xs text-[#144F75] hover:underline inline-flex items-center gap-1"><Users size={12} /> {open ? "Hide" : "Who's coming?"}</button>
      {open && data && (
        <div className="mt-2 text-sm bg-[#F9F7F3] rounded-lg p-3" data-testid={`event-attendees-${eventId}`}>
          {["going", "maybe", "no"].map((st) => (
            <div key={st} className="mb-1.5 last:mb-0">
              <span className="text-xs uppercase tracking-wide text-[#8FA89B]">{st === "no" ? "Can't make it" : st} ({data.counts?.[st] || 0})</span>
              {(data[st] || []).length > 0 && <span className="text-[#4A5D6B]"> — {data[st].map((a) => a.name).join(", ")}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveViewerCount({ eid, initial }) {
  const [n, setN] = useState(initial || 0);
  useEffect(() => {
    let active = true;
    const ping = async () => {
      try { const { data } = await api.post(`/group-events/${eid}/heartbeat`); if (active) setN(data.viewers); } catch { /* ignore */ }
    };
    ping();
    const t = setInterval(ping, 15000);
    return () => { active = false; clearInterval(t); };
  }, [eid]);
  return <span className="inline-flex items-center gap-1 text-xs text-[#4A5D6B]" data-testid={`live-viewers-${eid}`}><Eye size={12} /> {n} watching</span>;
}

function LiveEmbed({ url, testid }) {
  const embed = toEmbedUrl(url);
  const canFrame = embed !== url; // toEmbedUrl transformed it => embeddable (YouTube/Vimeo/Loom)
  if (canFrame) {
    return (
      <div className="aspect-video rounded-lg overflow-hidden bg-black" data-testid={testid}>
        <iframe src={embed} title="Live stream" className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testid}
      className="eyw-btn !py-2 text-sm w-full justify-center"><Radio size={15} /> Join the live stream</a>
  );
}

function GroupEvents({ group }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [form, setForm] = useState(null); // null=closed, {} = new
  const [liveForm, setLiveForm] = useState(null); // { eid, url }
  const { data, isLoading } = useQuery({
    queryKey: ["group-events", group.id],
    queryFn: () => api.get(`/groups/${group.id}/events`).then((r) => r.data),
  });
  const events = data?.events || [];
  const isHost = data?.is_host;
  const canGoLive = isHost || user?.role === "enhancer";
  const refresh = () => qc.invalidateQueries({ queryKey: ["group-events", group.id] });

  const createMut = useMutation({
    mutationFn: () => form._editId
      ? api.put(`/group-events/${form._editId}/series`, form)
      : api.post(`/groups/${group.id}/events`, form),
    onSuccess: () => { setForm(null); refresh(); toast.success(form._editId ? "Gathering updated" : "Gathering posted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const rsvpMut = useMutation({
    mutationFn: ({ eid, status }) => api.post(`/group-events/${eid}/rsvp`, { status }),
    onSuccess: refresh,
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const liveMut = useMutation({
    mutationFn: ({ eid, live_url, is_live, starting_soon, replay_url }) => api.put(`/group-events/${eid}/live`, { live_url, is_live, starting_soon: !!starting_soon, replay_url: replay_url || "" }),
    onSuccess: () => { setLiveForm(null); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: ({ eid, scope }) => api.delete(`/group-events/${eid}${scope === "series" ? "?scope=series" : ""}`),
    onSuccess: () => { refresh(); toast.success("Gathering removed"); },
  });

  const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "Date TBD";

  return (
    <div data-testid="group-events">
      {isHost && (
        <div className="mb-4">
          {form ? (
            <div className="eyw-card p-4 space-y-3" data-testid="group-event-form">
              {form._editId && <p className="text-xs text-[#B5794E]" data-testid="series-edit-note">Editing {form._isSeries ? "the whole series" : "this gathering"} — dates stay the same.</p>}
              <input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Gathering title" data-testid="group-event-title" className="eyw-input w-full" />
              {!form._editId && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <input type="datetime-local" value={form.starts_at_local || ""} onChange={(e) => setForm({ ...form, starts_at_local: e.target.value, starts_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="group-event-date" className="eyw-input w-full" />
                  <input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (or link)" data-testid="group-event-location" className="eyw-input w-full" />
                </div>
              )}
              {form._editId && <input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (or link)" data-testid="group-event-location" className="eyw-input w-full" />}
              <RichTextEditor value={form.description || ""} onChange={(v) => setForm((f) => ({ ...f, description: v }))} testid="group-event-desc" placeholder="Details (optional)" minHeight={90} enableBlocks={false} media={false} />
              <ImageUpload value={form.cover_image || ""} onChange={(u) => setForm({ ...form, cover_image: u })} label="Cover image (optional)" testid="group-event-cover" />
              {!form._editId && (
                <input value={form.live_url || ""} onChange={(e) => setForm({ ...form, live_url: e.target.value })} placeholder="Live stream URL (optional — YouTube Live / Zoom / external)" data-testid="group-event-live-url" className="eyw-input w-full" />
              )}
              {!form._editId && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <select value={form.recurrence || "none"} onChange={(e) => setForm({ ...form, recurrence: e.target.value })} data-testid="group-event-recurrence" className="eyw-input w-full">
                    <option value="none">Does not repeat</option>
                    <option value="daily">Repeats daily</option>
                    <option value="weekly">Repeats weekly</option>
                    <option value="monthly">Repeats monthly</option>
                  </select>
                  {form.recurrence && form.recurrence !== "none" && (
                    <input type="number" min={2} max={30} value={form.occurrences || 4} onChange={(e) => setForm({ ...form, occurrences: Number(e.target.value) })} data-testid="group-event-occurrences" placeholder="How many times" className="eyw-input w-full" />
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => createMut.mutate()} disabled={createMut.isPending || !form.title?.trim()} data-testid="group-event-save" className="eyw-btn !py-2 text-sm disabled:opacity-50">{createMut.isPending ? <Loader2 className="animate-spin" size={15} /> : <CalendarPlus size={15} />} {form._editId ? "Save changes" : "Post gathering"}</button>
                <button onClick={() => setForm(null)} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setForm({})} data-testid="group-event-add" className="eyw-btn !py-2 text-sm"><CalendarPlus size={15} /> Post a gathering</button>
          )}
        </div>
      )}
      {isLoading ? (
        <div className="grid place-items-center py-16 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
      ) : events.length === 0 ? (
        <p className="text-center text-[#8FA89B] text-sm py-14 eyw-card">No gatherings yet{isHost ? " — post the first one!" : "."}</p>
      ) : (
        <div className="space-y-3">
          {events.map((ev) => (
            <div key={ev.id} data-testid={`group-event-${ev.id}`} className="eyw-card overflow-hidden">
              {ev.cover_image && <img src={ev.cover_image} alt="" className="w-full h-40 object-cover" />}
              <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="font-serif text-lg text-[#1A242B]">{ev.title}</h4>
                  <p className="text-sm text-[#144F75] flex items-center gap-1.5 mt-0.5"><CalendarDays size={13} /> {fmt(ev.starts_at)}</p>
                  {ev.location && <p className="text-sm text-[#4A5D6B] flex items-center gap-1.5 mt-0.5"><MapPin size={13} /> {ev.location}</p>}
                  {ev.description && <RichContent text={ev.description} className="text-sm text-[#4A5D6B] mt-2" />}
                </div>
                {isHost && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setForm({ _editId: ev.id, _isSeries: !!ev.series_id, title: ev.title, description: ev.description, location: ev.location, cover_image: ev.cover_image })} data-testid={`group-event-edit-${ev.id}`} className="text-xs text-[#144F75] hover:underline">Edit</button>
                    <button onClick={() => window.confirm("Remove this gathering?") && delMut.mutate({ eid: ev.id, scope: "one" })} data-testid={`group-event-del-${ev.id}`} className="text-[#B9C4BD] hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              {ev.is_live && ev.live_url ? (
                <div className="mt-3" data-testid={`live-block-${ev.id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600"><span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> LIVE NOW</div>
                    <LiveViewerCount eid={ev.id} initial={ev.viewers} />
                  </div>
                  <LiveEmbed url={ev.live_url} testid={`live-embed-${ev.id}`} />
                </div>
              ) : ev.starting_soon ? (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#B5794E] bg-[#D98E73]/15 px-3 py-1.5 rounded-full" data-testid={`starting-soon-${ev.id}`}>
                  <Radio size={12} /> Starting soon — {ev.rsvp_counts?.going || 0} waiting
                </div>
              ) : ev.replay_url ? (
                <div className="mt-3" data-testid={`replay-block-${ev.id}`}>
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#144F75] mb-2"><PlayCircle size={13} /> Watch the replay</div>
                  <LiveEmbed url={ev.replay_url} testid={`replay-embed-${ev.id}`} />
                </div>
              ) : null}
              {isHost && ev.series_id && (
                <button onClick={() => window.confirm("Cancel EVERY date in this recurring series?") && delMut.mutate({ eid: ev.id, scope: "series" })} data-testid={`group-event-cancel-series-${ev.id}`} className="text-xs text-red-500 hover:underline mt-2">Cancel whole series</button>
              )}
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {[{ k: "going", l: "Going" }, { k: "maybe", l: "Maybe" }, { k: "no", l: "Can't make it" }].map((o) => (
                  <button key={o.k} onClick={() => rsvpMut.mutate({ eid: ev.id, status: o.k })} data-testid={`rsvp-${o.k}-${ev.id}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${ev.my_rsvp === o.k ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{o.l}</button>
                ))}
                <span className="text-xs text-[#8FA89B] ml-auto">{ev.rsvp_counts?.going || 0} going · {ev.rsvp_counts?.maybe || 0} maybe</span>
              </div>
              <div className="mt-3 pt-3 border-t border-[#E5E0D8]/60 flex items-center gap-4">
                <Link to={`/gatherings/${ev.slug || ev.id}`} data-testid={`group-event-open-${ev.id}`} className="text-xs text-[#144F75] hover:underline">Open event page</Link>
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/gatherings/${ev.slug || ev.id}`); toast.success("Link copied — share to invite friends!"); }} data-testid={`group-event-share-${ev.id}`} className="text-xs text-[#144F75] hover:underline inline-flex items-center gap-1"><Share2 size={12} /> Share</button>
                {canGoLive && liveForm?.eid !== ev.id && (
                  <button onClick={() => setLiveForm({ eid: ev.id, url: ev.live_url || "", replay: ev.replay_url || "" })} data-testid={`live-toggle-${ev.id}`} className="text-xs text-[#B5794E] hover:underline inline-flex items-center gap-1"><Radio size={12} /> {ev.is_live ? "Manage live" : "Go live"}</button>
                )}
              </div>
              {canGoLive && liveForm?.eid === ev.id && (
                <div className="mt-3 space-y-2 eyw-card p-3 bg-[#F9F7F3]" data-testid={`live-form-${ev.id}`}>
                  <input value={liveForm.url} onChange={(e) => setLiveForm({ ...liveForm, url: e.target.value })} placeholder="Paste YouTube Live / Zoom / stream URL" data-testid={`live-url-input-${ev.id}`} className="eyw-input w-full text-sm" />
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => liveMut.mutate({ eid: ev.id, live_url: liveForm.url, is_live: true, starting_soon: false, replay_url: liveForm.replay || "" })} disabled={liveMut.isPending || !liveForm.url.trim()} data-testid={`go-live-${ev.id}`} className="eyw-btn !py-1.5 text-xs disabled:opacity-50"><Radio size={13} /> Go live now</button>
                    <button onClick={() => liveMut.mutate({ eid: ev.id, live_url: liveForm.url, is_live: false, starting_soon: true, replay_url: liveForm.replay || "" })} disabled={liveMut.isPending} data-testid={`starting-soon-btn-${ev.id}`} className="eyw-btn-ghost !py-1.5 text-xs">Mark "starting soon"</button>
                    {(ev.is_live || ev.starting_soon) && <button onClick={() => liveMut.mutate({ eid: ev.id, live_url: liveForm.url, is_live: false, starting_soon: false, replay_url: liveForm.replay || "" })} disabled={liveMut.isPending} data-testid={`stop-live-${ev.id}`} className="eyw-btn-ghost !py-1.5 text-xs">End broadcast</button>}
                    <button onClick={() => setLiveForm(null)} className="eyw-btn-ghost !py-1.5 text-xs">Close</button>
                  </div>
                  <input value={liveForm.replay || ""} onChange={(e) => setLiveForm({ ...liveForm, replay: e.target.value })} placeholder="Replay link (shown after the broadcast ends)" data-testid={`replay-url-input-${ev.id}`} className="eyw-input w-full text-sm" />
                  <p className="text-[11px] text-[#8FA89B]">YouTube Live &amp; Vimeo play inline; Zoom &amp; other links open in a new tab. Add a replay link so members who missed it can watch later.</p>
                </div>
              )}
              {isHost && <EventAttendees eventId={ev.id} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GroupDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tabSel, setTab] = useState("");

  const { data: group, isLoading, error } = useQuery({
    queryKey: ["group", slug],
    queryFn: () => api.get(`/groups/${slug}`).then((r) => r.data),
    retry: false,
  });

  const joinMut = useMutation({
    mutationFn: () => api.post(`/groups/${group.id}/join`),
    onSuccess: ({ data }) => {
      qc.invalidateQueries({ queryKey: ["group", slug] });
      toast.success(data.status === "pending" ? "Request sent — a host will approve you soon." : "Welcome!");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail?.message || e.response?.data?.detail)),
  });
  const leaveMut = useMutation({
    mutationFn: () => api.post(`/groups/${group.id}/leave`),
    onSuccess: () => { toast.success("You left the group."); qc.invalidateQueries({ queryKey: ["group", slug] }); },
  });

  if (isLoading) return <div className="grid place-items-center py-40 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>;
  if (error || !group) return <div className="text-center py-40 text-[#4A5D6B]">Group not found. <Link to="/community" className="text-[#144F75]">Back to Community</Link></div>;

  const isMember = group.my_status === "active" || group.is_host;
  const tab = tabSel || (group.program ? "program" : "discussion");
  const isPending = group.my_status === "pending";

  return (
    <div data-testid="group-detail-page" className="max-w-[1000px] mx-auto px-6 lg:px-10 py-10">
      <Link to="/community" className="inline-flex items-center gap-1 text-sm text-[#4A5D6B] hover:text-[#144F75] mb-6"><ArrowLeft size={15} /> Community</Link>

      <div className="eyw-card overflow-hidden mb-8">
        <div className="relative h-44 bg-[#E5E0D8]">
          {group.image && <img src={group.image} alt={group.name} className="h-full w-full object-cover" />}
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-11 w-11 rounded-full bg-[#144F75]/10 text-[#144F75]"><GroupIcon name={group.icon} size={22} /></span>
              <div>
                <h1 className="font-serif text-3xl text-[#1A242B]">{group.name}</h1>
                <p className="text-xs text-[#8FA89B]">{group.member_count} member{group.member_count === 1 ? "" : "s"} · {group.private ? "Private program group" : group.access === "request" ? "Request to join" : group.access === "tier_gated" ? "Members only" : "Open"}</p>
              </div>
            </div>
            {group.private ? null : isMember ? (
              <button onClick={() => leaveMut.mutate()} data-testid="leave-group" className="eyw-btn-ghost !py-2 text-sm text-red-600"><LogOut size={14} /> Leave</button>
            ) : isPending ? (
              <span className="text-sm text-[#b8694f] font-medium">Request pending approval</span>
            ) : !user ? (
              <button onClick={() => nav("/login")} className="eyw-btn !py-2 text-sm">Log in to join</button>
            ) : (
              <button onClick={() => joinMut.mutate()} disabled={joinMut.isPending} data-testid="join-group" className="eyw-btn !py-2 text-sm disabled:opacity-60">
                {joinMut.isPending && <Loader2 className="animate-spin" size={14} />} {group.access === "request" ? "Request to join" : "Join group"}
              </button>
            )}
          </div>
          <RichContent text={group.description} className="text-[#4A5D6B] mt-4 leading-relaxed" testid="group-description" />
        </div>
      </div>

      {!isMember ? (
        <div className="eyw-card p-10 text-center">
          <span className="grid place-items-center h-14 w-14 rounded-full bg-[#144F75]/10 text-[#144F75] mx-auto mb-4">
            {group.access === "request" ? <Lock size={26} /> : <UserCheck size={26} />}
          </span>
          <h3 className="font-serif text-2xl text-[#1A242B]">Members only</h3>
          <p className="text-[#4A5D6B] mt-2 max-w-md mx-auto">{group.access === "request" ? "Request to join and a host will welcome you in." : "Join this group to see discussions and members."}</p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-6 overflow-x-auto scroll-x-hidden pb-1 -mx-1 px-1">
            {[...(group.program ? [{ k: "program", l: "Program", Ic: CalendarDays }] : []), { k: "discussion", l: "Discussion", Ic: MessageCircle }, { k: "chat", l: "Chat", Ic: MessagesSquare }, { k: "events", l: "Events", Ic: CalendarDays }, { k: "members", l: "Members", Ic: Users }].map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`group-tab-${t.k}`}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm transition-colors ${tab === t.k ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>
                <t.Ic size={15} /> {t.l}
              </button>
            ))}
          </div>
          {tab === "program" ? <ProgramPanel group={group} /> : tab === "discussion" ? <Discussion group={group} /> : tab === "chat" ? <GroupChat group={group} /> : tab === "events" ? <GroupEvents group={group} /> : <Members group={group} />}
        </>
      )}
    </div>
  );
}
